import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '../../auth/auth.service';
import { UserInvitationService } from '../../auth/user-invitation.service';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { SnackbarService } from '../../service/snackbar.service';

export interface EmailLinkLoginForm {
  email: FormControl<string>;
  firstname: FormControl<string>;
  lastname: FormControl<string>;
}

@Component({
  selector: 'app-user-invitation-login-page',
  templateUrl: './user-invitation-login-page.component.html',
  styleUrls: ['./user-invitation-login-page.component.scss'],
})
export class UserInvitationLoginPageComponent {
  loginLink = '';
  isValidLoginLink = false;
  isExistingUser = false;
  form: FormGroup<EmailLinkLoginForm>;
  formHelper: FormHelper<EmailLinkLoginForm>;

  constructor(
    private authService: AuthService,
    private userInvitationService: UserInvitationService,
    private router: Router,
    private snackBar: SnackbarService,
    private translateService: TranslateService,
    formHelperFactory: FormHelperFactory,
  ) {
    this.loginLink = window.location.href;
    this.isValidLoginLink = this.authService.isValidSignInWithEmailLink(this.loginLink);

    // If the user already exists in our app, only the email needs to be filled out.
    this.isExistingUser = this.userInvitationService.isSignInWithEmailLinkForExistingUser(this.loginLink);

    const fb = new FormBuilder();
    this.form = fb.group(
      {
        email: fb.nonNullable.control('', [Validators.required, Validators.email]),
        firstname: fb.nonNullable.control('', this.isExistingUser ? [] : [Validators.required]),
        lastname: fb.nonNullable.control('', this.isExistingUser ? [] : [Validators.required]),
      },
      { updateOn: 'submit' },
    );
    this.formHelper = formHelperFactory.create(this.form);

    // Redirect after login
    authService.currentUser$.pipe(takeUntilDestroyed()).subscribe(user => {
      if (user) {
        this.router.navigateByUrl(user.redirectUrlAfterLogin);
      }
    });
  }

  async signIn() {
    this.formHelper.trackSubmitted();
    if (!this.form.valid) {
      return;
    }

    try {
      await this.userInvitationService.signInWithEmailLink(this.loginLink, this.form.getRawValue());
      this.snackBar.showSuccess(this.translateService.instant('PAGE.USER_INVITATION_LOGIN.NOTIFICATION.ACCOUNT_CONFIRMED'));
    } catch (e) {
      this.snackBar.showError(this.translateService.instant('PAGE.USER_INVITATION_LOGIN.NOTIFICATION.ACCOUNT_NOT_CONFIRMED'));
    }
  }
}
